/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2018 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import React, { useEffect } from 'react';
import classNames from 'classnames';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';

import Button from '@react/react-spectrum/Button';
import Delete from '@react/react-spectrum/Icon/Delete';
import Link from '@react/react-spectrum/Link';
import Search from '@react/react-spectrum/Search';
import { Tab, TabList } from '@react/react-spectrum/TabList';

import { defineMessages, injectIntl } from '@nebula/izzy/intl';

import {
  Composable,
  HeaderBar,
  HeaderBarButtons,
  ViewTable
} from '@nebula/luna/dist/core/composable';
import IconTooltip from '@nebula/luna/dist/core/icon-tooltip';
import { VColumn } from '@nebula/luna/dist/core/vtable';
import ModalDialog from '@nebula/luna/dist/core/modal-dialog';
import GLOBAL_MSG from '@nebula/luna/dist/global.strings';
// TODO: replace with react spectrum ActionBar when available
import ActionBar from '@nebula/luna/dist/temp/action-bar';

import { isSessionExpiring } from '../../common/util/session.utils';
import loadingEmptyRenderer from '../loading.empty.renderer';
import styles from './index.styl';
import { useOmega } from '../common/omega';
import useMetrics from '../common/metrics';

const MSG = defineMessages({
  all: {
    id: 'gemini.session.list.all',
    defaultMessage: 'All'
  },
  archive: {
    id: 'gemini.session.list.archive',
    defaultMessage: 'Archive'
  },
  create: {
    id: 'gemini.session.list.create',
    defaultMessage: 'Create Session'
  },
  created: {
    id: 'gemini.session.list.column.created',
    defaultMessage: 'Created'
  },
  deleteTitleSingle: {
    id: 'gemini.session.delete.title.single',
    defaultMessage: 'Delete Session?'
  },
  deleteTitleMultiple: {
    id: 'gemini.session.delete.title.multiple',
    defaultMessage: 'Delete Sessions?'
  },
  deleteBodySingle: {
    id: 'gemini.session.delete.body.single',
    defaultMessage: 'Are you sure? This will delete "{number}" session'
  },
  deleteBodyMultiple: {
    id: 'gemini.session.delete.body.multiple',
    defaultMessage: 'Are you sure? This will delete "{number}" sessions'
  },
  emptyDescription: {
    id: 'gemini.session.list.empty.description',
    defaultMessage: 'Create a Session by clicking the blue button above.'
  },
  emptyHeading: {
    id: 'gemini.session.list.empty.header',
    defaultMessage: 'There\'s nothing in here.'
  },
  emptyRecentDescription: {
    id: 'gemini.session.list.empty.recent.description',
    defaultMessage: 'Recent opened sessions will appear here.'
  },
  emptyRecentHeading: {
    id: 'gemini.session.list.empty.recent.header',
    defaultMessage: 'There\'s nothing in here yet.'
  },
  expiring: {
    id: 'gemini.session.list.expiring',
    defaultMessage: 'Deleting Soon'
  },
  expiringInfo: {
    id: 'gemini.session.list.expiringInfo',
    defaultMessage: 'Sessions are deleted after 30 days. See documentation for more information.'
  },
  heading: {
    id: 'gemini.session.list.heading',
    defaultMessage: 'Sessions'
  },
  name: {
    id: 'gemini.session.list.column.name',
    defaultMessage: 'Name'
  },
  owner: {
    id: 'gemini.session.list.column.owner',
    defaultMessage: 'Owner'
  },
  recent: {
    id: 'gemini.session.list.recent',
    defaultMessage: 'Recent'
  },
  searchPlaceholder: {
    id: 'gemini.session.list.search.placeholder',
    defaultMessage: 'Search Sessions'
  }
});

const pad = (n, length = 2) => n.toString().padStart(length, '0');

const createdCellRenderer = (nowTs, notice, info, expiringSessions) => ({
  cellData
}) => {
  const createdTsDate = new Date(cellData);
  const expiring = isSessionExpiring(nowTs, createdTsDate.getTime());
  const createdFormatted = `${createdTsDate.getFullYear()}-${pad(createdTsDate.getMonth() + 1)}-`
    + `${pad(createdTsDate.getDate())} ${pad(createdTsDate.getHours())}:`
    + `${pad(createdTsDate.getMinutes())}:${pad(createdTsDate.getSeconds())}`;

  return !expiring || !expiringSessions ? createdFormatted : (
    <div className={styles.createdCell}>
      {`${createdFormatted}`}
      <div className="notice">
        <IconTooltip
          info={info}
          trigger="hover"
        />
        {notice}
      </div>
    </div>
  );
};

const ownerCellRenderer = ({
  cellData,
  rowData
}) => (rowData.lastName ? `${rowData.firstName} ${rowData.lastName}`
  : rowData.email || cellData);

const linkCellRenderer = onClick => ({
  cellData,
  rowData
}) => (cellData ? <Link onClick={e => onClick(rowData, e)}>{String(cellData)}</Link> : '');

const actionButtons = {
  children: [
    <Button
      label="Delete"
      key="delete"
      value="delete"
      data-test-id="deleteButton"
      icon={<Delete />}
    />
  ]
};

const SessionList = ({
  className,
  createWidget,
  data,
  deleting = false,
  emptyImage,
  expiringSessions,
  izzy,
  loading,
  onCancelDelete,
  onChangeSearch,
  onClickCreate,
  onConfirmDelete,
  onNameClick,
  onSelectTab,
  onSubmitSearch,
  search,
  selectedData = [],
  selectedRows = [],
  selectedTabIndex,
  settingsLoaded,
  onActionBarClick,
  onActionBarChange,
  ...props
}) => {
  useOmega('assurance', 'sessions list');
  const metrics = useMetrics();
  useEffect(() => {
    metrics.analytics.trackPage();
  }, []);

  const nowTs = Date.now();
  const expiringNotice = izzy(MSG.expiring);
  const expiringInfo = izzy(MSG.expiringInfo);
  const noRowsRenderer = selectedTabIndex === 0
    ? loadingEmptyRenderer(loading,
      {
        description: izzy(MSG.emptyDescription),
        heading: izzy(MSG.emptyHeading),
        illustration: emptyImage
      })
    : loadingEmptyRenderer(loading || !settingsLoaded,
      {
        description: izzy(MSG.emptyRecentDescription),
        heading: izzy(MSG.emptyRecentHeading),
        illustration: emptyImage
      });

  return (
    <div
      data-omega-feature="sessions list"
      data-omega-widget="sessions list"
      style={{ height: '100%', width: '100%' }}
    >
      <Composable
        className={classNames(
          className,
          styles.root,
        )}
      >
        <HeaderBar
          className={styles.header}
          label={izzy(MSG.heading)}
        >
          <HeaderBarButtons>
            <div>
              <Button
                data-omega-element="start create session button"
                data-test-id="createButton"
                label={izzy(MSG.create)}
                onClick={onClickCreate}
                variant="cta"
              />
            </div>
          </HeaderBarButtons>
        </HeaderBar>
        <div>
          <TabList
            onChange={onSelectTab}
            selectedIndex={selectedTabIndex}
          >
            <Tab label={izzy(MSG.all)} />
            <Tab label={izzy(MSG.recent)} />
          </TabList>
        </div>
        <div>
          <Search
            data-test-id="searchSessions"
            placeholder={izzy(MSG.searchPlaceholder)}
            value={search}
            onChange={onChangeSearch}
            onSubmit={onSubmitSearch}
          />
        </div>
        <div
          className={styles['flex-table']}
        >
          <AutoSizer style={{ height: '100%', width: '100%' }}>
            {({ height, width }) => (
              <React.Fragment>
                <ViewTable
                  className={styles['sessions-table']}
                  height={height}
                  noRowsRenderer={noRowsRenderer}
                  data={loading ? [] : data}
                  selectedRows={selectedRows}
                  width={width}
                  {...props}
                >
                  <VColumn
                    headerClassName="is-sorted-desc"
                    cellRenderer={linkCellRenderer(onNameClick)}
                    dataKey="name"
                    disableSort={false}
                    flexGrow={1}
                    label={izzy(MSG.name)}
                    width={150}
                  />
                  <VColumn
                    cellRenderer={ownerCellRenderer}
                    dataKey="createdById"
                    disableSort={false}
                    flexGrow={1}
                    label={izzy(MSG.owner)}
                    width={100}
                  />
                  <VColumn
                    cellRenderer={
                      createdCellRenderer(nowTs, expiringNotice, expiringInfo, expiringSessions)
                    }
                    dataKey="createdTs"
                    disableSort={false}
                    flexGrow={1}
                    label={izzy(MSG.created)}
                    width={250}
                  />
                </ViewTable>
                { selectedRows.length > 0 && (
                  <div className={styles.actionbar}>
                    <ActionBar
                      onChange={onActionBarChange}
                      onActionClick={onActionBarClick}
                      open
                      selectedLabel={`${selectedRows.length} Selected`}
                      style={{ textAlign: 'left' }}
                      {...actionButtons}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </AutoSizer>
        </div>
      </Composable>
      {createWidget}
      {selectedData.length > 0 && (
        <ModalDialog
          className={styles.dialog}
          mode="alert"
          title={selectedData.length === 1
            ? izzy(MSG.deleteTitleSingle)
            : izzy(MSG.deleteTitleMultiple)}
          variant="destructive"
          confirmLabel={izzy(GLOBAL_MSG.confirm)}
          cancelLabel={izzy(GLOBAL_MSG.cancel)}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          open={deleting}
        >
          {selectedData.length === 1 && (
            <span data-test-id="deleteBodySingle">{izzy(MSG.deleteBodySingle, { number: selectedData.length })}</span>
          )}
          {selectedData.length > 1 && (
            <span data-test-id="deleteBodyMultiple">{izzy(MSG.deleteBodyMultiple, { number: selectedData.length })}</span>
          )}
        </ModalDialog>
      )}
    </div>
  );
};

SessionList.displayName = 'SessionList';

export default injectIntl(SessionList);
